<template>
  <div class="page" :style="themeStyle">
    <div v-if="!loaded" class="loading">Loading…</div>
    <div v-if="loaded" class="root">

      <!-- CSS geometric canvas background -->
      <div class="canvas-bg" aria-hidden="true">
        <div class="geo geo-1"></div>
        <div class="geo geo-2"></div>
        <div class="geo geo-3"></div>
        <div class="geo geo-4"></div>
        <div class="geo geo-5"></div>
        <div class="geo geo-6"></div>
      </div>

      <!-- Nav -->
      <nav class="nav">
        <img :src="lightLogoUrl" class="nav-logo" />

      </nav>

      <!-- Hero text — floats above canvas -->
      <section class="hero">
  
        <h1 class="hero-h1" v-html="heroH1Html"></h1>
        <p class="hero-sub">{{ heroBody }}</p>
        <div class="hero-btns">
          <a class="btn-a" :href="applyRightLink">{{ heroPrimaryText }}</a>
          <a v-if="applyLeftLink" class="btn-b" :href="applyLeftLink">{{ heroSecondaryText }}</a>
        </div>
      </section>

      <!-- Profile cards float like pinned notes on the canvas -->
      <section class="board">

        <div class="card-pin lo-pin">
          <div class="pin-dot lo-dot" aria-hidden="true"></div>
          <div class="pin-card">
            <div class="pc-header lo-header">
              <span class="pc-tag">Loan Officer</span>
            </div>
            <div class="pc-body">
              <img v-if="loinfo && loinfo.lo_image" :src="loinfo.lo_image" class="pc-avatar" />
              <div class="pc-info">
                <div class="pc-name">{{ loName }}</div>
                <div class="pc-role">{{ loTitle }}</div>
                <div v-if="loinfo && loinfo.nmls" class="pc-nmls">NMLS {{ (loinfo.nmls || "").trim() }}</div>
              </div>
            </div>
            <div class="pc-contacts">
              <a v-if="loinfo && loinfo.phone_number" :href="'tel:' + loinfo.phone_number" class="pc-contact">{{ loinfo.phone_number }}</a>
              <a v-if="loinfo && loinfo.email" :href="'mailto:' + loinfo.email" class="pc-contact">{{ loinfo.email }}</a>
            </div>
       
          </div>
        </div>

        <div class="card-pin re-pin">
          <div class="pin-dot re-dot" aria-hidden="true"></div>
          <div class="pin-card">
            <div class="pc-header re-header">
              <span class="pc-tag re-pc-tag">{{ realtor?.partner_title || 'Partner' }}</span>
            </div>
            <div class="pc-body">
              <img v-if="realtor && realtor.headshot_url" :src="realtor.headshot_url" class="pc-avatar" />
              <div class="pc-info">
                <div class="pc-name">{{ realtorName }}</div>
                <div class="pc-role">{{ realtor && realtor.brokerage_name ? realtor.brokerage_name : "" }}</div>
                <div v-if="realtor && realtor.nmls" class="pc-nmls">NMLS {{ (realtor.nmls || "").trim() }}</div>
              </div>
            </div>
            <div class="pc-contacts">
              <a v-if="realtor && realtor.phone_number" :href="'tel:' + realtor.phone_number" class="pc-contact re-contact">{{ realtor.phone_number }}</a>
              <a v-if="realtor && realtor.email" :href="'mailto:' + realtor.email" class="pc-contact re-contact">{{ realtor.email }}</a>
              <a v-if="realtor && realtor.website_url" :href="realtor.website_url" class="pc-contact re-contact" target="_blank" rel="noreferrer">Website →</a>
            </div>
          </div>
        </div>

      </section>

    </div>
  </div>
</template>

<script>
import coBrandMixin from "../../mixins/coBrandMixin.js";
const BRAND_COLORS = {
  zonalending:        { primary: "#eb610a", accent: "#646363" },
  radiantmortgage:    { primary: "#1C1F39", accent: "#DB9457" },
  conradmortgage:     { primary: "#4a4b4b", accent: "#d4af37" },
  theimgteam:         { primary: "#ff6633", accent: "#4a4c54" },
  responsivemortgage: { primary: "#07212c", accent: "#FF7F47" },
  kpmortgage:         { primary: "#0D2821", accent: "#34BFC6" },
  ignitemortgage:     { primary: "#000000", accent: "#00b1eb" },
  goldstarfinancial:  { primary: "#0b1726", accent: "#c69c39" },
};
const DBA_BASE = "https://api.goldstarfinancial.com/DBA";
function dbaLogo(brandKey, variant = "primary") { return `${DBA_BASE}/${brandKey}/${variant}.png`; }
function getBrandKey(email) {
  if (!email) return null;
  const d = email.slice(email.indexOf("@") + 1).toLowerCase();
  return d.split(".")[0] || null;
}
export default {
  name: "CoBrandCanvas",
  mixins: [coBrandMixin],
  computed: {
    brandKey() { const k = getBrandKey(this.loinfo?.email); return (k && BRAND_COLORS[k]) ? k : "goldstarfinancial"; },
    palette() { return BRAND_COLORS[this.brandKey] || BRAND_COLORS.goldstarfinancial; },
    themeStyle() { return { "--p": this.palette.primary, "--a": this.palette.accent }; },
    lightLogoUrl() { return dbaLogo(this.brandKey, "primary"); },
    
    heroH1Html() {
      const h = (this.realtor?.header1 || this.loinfo?.header1 || "").trim();
      if (!h) return "Ready to get<br/><em>started?</em>";
      const parts = h.split(",");
      if (parts.length > 1) {
        const a = parts[0].trim();
        const b = parts.slice(1).join(",").trim();
        return `${a}<br/><em>${b}</em>`;
      }
      return h;
    },
    
    heroHeading() { return this.loinfo?.text_section_heading || "About"; },
    heroPrimaryText() { return this.loinfo?.right_button_text || "Apply Now"; },
    heroSecondaryText() { return this.loinfo?.left_button_text || "Schedule Appointment"; },
    heroBody() {
      const m = (this.realtor?.main || this.loinfo?.main || "").trim();
      if (m) return m;
      const p = [this.loinfo?.text_section_body_p1, this.loinfo?.text_section_body_p2].filter(Boolean).join(" ").trim();
      return p || this.loinfo?.footer_slogan || "Start your application in minutes and we'll guide you from application through closing.";
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;700&family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,700;1,9..144,300;1,9..144,700&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;700;800&family=Epilogue:wght@300;400;500&display=swap');

.page {
  min-height: 100vh;
  background: #f8f6f2;
  color: #1a1710;
  font-family: 'Epilogue', sans-serif;
  --p: #0b1726;
  --a: #c69c39;
  --bg: #f8f6f2;
  --white: #ffffff;
  --hairline: rgba(26,23,16,0.09);
  --muted: #7a7464;
  --p-tint: color-mix(in srgb, var(--p) 7%, #f8f6f2);
  --a-tint: color-mix(in srgb, var(--a) 12%, #f8f6f2);
  overflow-x: hidden;
  position: relative;
}
.root { position: relative; z-index: 1; display: flex; flex-direction: column; min-height: 100vh; }

/* GEOMETRIC CANVAS BG — pure CSS shapes */
.canvas-bg { position: fixed; inset: 0; pointer-events: none; z-index: 0; overflow: hidden; }
.geo { position: absolute; }
/* Large circle — bottom right */
.geo-1 { width: 560px; height: 560px; border-radius: 50%; border: 1.5px solid color-mix(in srgb, var(--p) 8%, transparent); bottom: -160px; right: -120px; }
/* Rotated square — top right */
.geo-2 { width: 280px; height: 280px; border: 1.5px solid color-mix(in srgb, var(--a) 20%, transparent); top: 40px; right: 80px; transform: rotate(20deg); }
/* Solid accent rectangle */
.geo-3 { width: 180px; height: 8px; background: color-mix(in srgb, var(--a) 18%, transparent); top: 160px; left: -30px; transform: rotate(-8deg); border-radius: 4px; }
/* Small filled circle */
.geo-4 { width: 48px; height: 48px; border-radius: 50%; background: color-mix(in srgb, var(--p) 8%, transparent); top: 30%; left: 55%; }
/* Big arc — top left */
.geo-5 { width: 320px; height: 320px; border-radius: 50%; border: 1.5px solid color-mix(in srgb, var(--p) 7%, transparent); top: -100px; left: -80px; }
/* Horizontal rule line */
.geo-6 { width: 100%; height: 1px; background: linear-gradient(90deg, transparent, color-mix(in srgb, var(--a) 25%, transparent), transparent); top: 48%; }

/* NAV */
.nav { position: relative; z-index: 10; display: flex; align-items: center; justify-content: space-between; padding: 24px 52px; border-bottom: 1px solid var(--hairline); background: rgba(248,246,242,0.8); backdrop-filter: blur(12px); }
.nav-logo { width: 160px; max-height:80px;  height: auto; object-fit: contain; }
.nav-cta { font-family: 'Syne', sans-serif; font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; text-decoration: none; color: var(--white); background: var(--p); padding: 11px 24px; border-radius: 6px; transition: opacity 0.18s; }
.nav-cta:hover { opacity: 0.85; }

/* HERO */
.hero { position: relative; z-index: 1; padding: 80px 52px 52px; max-width: 700px;align-self: center; }
.hero-eyebrow { font-size: 10px; letter-spacing: 0.28em; text-transform: uppercase; color: color-mix(in srgb, var(--a) 80%, #1a1710); margin-bottom: 16px; font-weight: 400; }
.hero-h1 { font-family: 'Syne', sans-serif; font-size: clamp(42px, 6vw, 76px); font-weight: 800; line-height: 1.0; color: var(--p); margin: 0 0 22px; letter-spacing: -0.02em; }
.hero-h1 em { font-style: italic; color: var(--a); font-family: 'Epilogue', sans-serif; font-weight: 300; }
.hero-sub { font-size: 15px; line-height: 1.76; color: var(--muted);  margin-bottom: 32px; font-weight: 300; }
.hero-btns { display: flex; gap: 10px; flex-wrap: wrap;  justify-content: center;}
.btn-a { font-family: 'Syne', sans-serif; font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; text-decoration: none; background: var(--p); color: #fff; padding: 14px 30px; border-radius: 6px; transition: transform 0.18s, box-shadow 0.18s;  width:55%; }
.btn-a:hover { transform: translateY(-2px); box-shadow: 0 8px 24px color-mix(in srgb, var(--p) 26%, transparent); }
.btn-b { font-size: 14px; font-weight: 400; text-decoration: none; color: var(--muted); border: 1px solid var(--hairline); background: rgba(255,255,255,0.5); padding: 14px 26px; border-radius: 6px; transition: background 0.18s; }
.btn-b:hover { background: rgba(255,255,255,0.9); }

/* BOARD — cards laid out like an art arrangement */
.board { position: relative; z-index: 1; display: flex; gap: 20px; padding: 24px 52px 64px; flex-wrap: wrap;align-self: center; }

.card-pin { position: relative; flex: 0 0 auto; width: 300px; }
.pin-dot { position: absolute; top: -10px; left: 50%; transform: translateX(-50%); width: 16px; height: 16px; border-radius: 50%; box-shadow: 0 2px 8px rgba(0,0,0,0.2); z-index: 2; }
.lo-dot { background: var(--p); }
.re-dot { background: var(--a); }

.pin-card { background: #fff; border-radius: 16px; border: 1px solid var(--hairline); box-shadow: 0 4px 32px rgba(26,23,16,0.10), 0 1px 4px rgba(26,23,16,0.05); overflow: hidden; transition: transform 0.2s, box-shadow 0.2s; }
.pin-card:hover { transform: rotate(0deg) translateY(-4px); box-shadow: 0 12px 40px rgba(26,23,16,0.15); }

.lo-pin { transform: rotate(-1.2deg); }
.re-pin { transform: rotate(0.8deg); margin-top: 20px; }
.lo-pin:hover, .re-pin:hover { transform: rotate(0deg); }

.pc-header { padding: 16px 18px 12px; }
.lo-header { background: var(--p-tint); border-bottom: 1px solid var(--hairline); }
.re-header { background: var(--a-tint); border-bottom: 1px solid rgba(198,156,57,0.12); }
.pc-tag { font-family: 'Syne', sans-serif; font-size: 9px; font-weight: 700; letter-spacing: 0.22em; text-transform: uppercase; color: var(--p); }
.re-pc-tag { color: color-mix(in srgb, var(--a) 80%, #1a1710); }

.pc-body { display: flex; gap: 14px; align-items: flex-start; padding: 16px 18px 10px; }
.pc-avatar { width: 52px; border-radius: 10px; object-fit: cover; flex-shrink: 0; border: 1px solid var(--hairline); }
.pc-name { font-family: 'Syne', sans-serif; font-size: 17px; font-weight: 700; color: var(--p); margin-bottom: 2px; line-height: 1.2; }
.pc-role { font-size: 11px; color: var(--muted); margin-bottom: 2px; }
.pc-nmls { font-size: 10px; color: color-mix(in srgb, var(--muted) 65%, transparent); }

.pc-contacts { padding: 0 18px 10px; display: flex; flex-direction: column; gap: 4px; }
.pc-contact { font-size: 11px; text-decoration: none; color: var(--muted); transition: color 0.18s; }
.pc-contact:hover { color: var(--p); }
.re-contact:hover { color: color-mix(in srgb, var(--a) 80%, #1a1710); }

.pc-cta { display: block; margin: 0 14px 14px; text-align: center; text-decoration: none; font-family: 'Syne', sans-serif; font-size: 10px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; padding: 11px; border-radius: 10px; transition: opacity 0.18s; }
.lo-cta { background: var(--p); color: #fff; }
.lo-cta:hover { opacity: 0.85; }

@media (max-width: 700px) {
  .hero { padding: 52px 24px 40px; }
  .nav { padding: 18px 24px; }
  .board { padding: 16px 24px 48px; justify-content: center; }
  .card-pin { width: 100%; max-width: 340px; }
  .lo-pin, .re-pin { transform: none; margin-top: 0; }
}
</style>
